import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { configureTracking, traceVisit, finalizeCurrent } from '../tracking/searchTrace'
import { Inbox, FlaskConical, Settings as SettingsIcon, Database, LogOut, Lock, ChevronDown, ChevronRight } from 'lucide-react'
import { useAuth } from '../auth'
import ActAsPicker from './ActAsPicker'
import TweaksPanel from './TweaksPanel'
import QuietMenu from './QuietMenu'
import { useProductName, saveProductName, FONTS } from '../productName'
import { useNavColor } from '../navColor'
import { useNavBanner } from '../navBanner'
import { deriveNewPipColor } from '../newPipColor'
import { isPlayheadTenant } from '../playhead'

// Top-level destinations — the PLACES you live in. Work Product is NOT
// here: it's a utility ("Pulls"), rendered separately on the right as a
// bordered tool. Pipeline / Prompts / Observability / Artifacts live under
// Studio (admin tooling consolidated there).
// `prominent` = the client-facing pages (Threads, Stats). The rest are
// me-facing admin pages: rendered in a see-through dark-brown so they
// recede into the header background (Emerson 2026-08-07).
// Top-level nav (Ken 2026-10-22): Matters · Walls · Quiet · wip. Stats, Playbook,
// Accounting moved INTO the "wip" dropdown (wipItems below); Quiet + wip are
// rendered specially in the nav. Settings/Imports/Studio stay admin-only.
const tabs: Array<{
  to: string; label: string; icon: unknown;
  prominent?: boolean; adminOnly?: boolean; wallsTab?: boolean
}> = [
  { to: '/workspace', label: 'Main', icon: Inbox, prominent: true },   // Matters (label = pageMainName)
  // Privacy → "Walls" (Ken 2026-10-22; page heading was already "Walls").
  { to: '/privacy', label: 'Walls', icon: Lock, wallsTab: true },
  // Settings is operator tooling — hidden from client end-users (Heather /
  // Maria don't need it); shown to studio + superuser (Heather 2026-08-18).
  { to: '/settings', label: 'Settings', icon: SettingsIcon, adminOnly: true },
  { to: '/imports/v1', label: 'Imports', icon: Database, adminOnly: true },
  { to: '/studio', label: 'Studio', icon: FlaskConical, adminOnly: true },
]

// "wip" dropdown subpages (Ken 2026-10-22). All visible to every user — incl.
// Accounting, which graduated from admin-only so Heather (and others) can see it.
// Stats (was Numbers) + Playbooks (was Playbook) are the existing pages, moved in;
// Internal Outreach / Org Chart / Break Room are "coming soon" stubs.
const wipItems: Array<{ to: string; label: string }> = [
  { to: '/stats', label: 'Stats' },
  { to: '/work-product', label: 'Playbooks' },
  { to: '/internal-outreach', label: 'Internal Outreach' },
  { to: '/org-chart', label: 'Org Chart' },
  { to: '/accounting', label: 'Accounting' },
  { to: '/break-room', label: 'Breaks' },
]

// Admin dropdown items for KEN/playhead (Ken 2026-10-22) — the admin tools that
// are separate top-level tabs on MOVU collapse into one caret-driven "admin"
// page on KEN. Imports (the v1-legacy import tool) is dropped: the KEN demo
// doesn't pull from v1, so it has no use here.
const adminItems: Array<{ to: string; label: string }> = [
  { to: '/studio', label: 'Studio' },
  { to: '/settings', label: 'Settings' },
]

// A nav-dropdown item is either a leaf (a page link) or a submenu that opens a
// side flyout of its own pages (KEN 2026-10-23: "admin" is now a submenu nested
// inside the "wip" dropdown — doubly-nested).
type NavLeaf = { to: string; label: string }
type NavNode = NavLeaf | { label: string; children: NavLeaf[] }

// A nested submenu row inside a NavDropdown: opens a flyout to the right on
// hover or click. onNavigate closes the parent dropdown when a child is picked.
function NavSubmenu({ node, chromeFont, onNavigate }: {
  node: { label: string; children: NavLeaf[] }
  chromeFont: string
  onNavigate: () => void
}) {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const onSection = node.children.some((it) => location.pathname.startsWith(it.to))
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-selected={onSection ? 'true' : undefined}
        className="flex w-full items-center justify-between rounded px-2.5 py-1.5 text-[12px] me-text hover:bg-[color:var(--me-selected)] data-[selected]:bg-[color:var(--me-selected)]"
        style={{ fontFamily: chromeFont }}
      >
        <span>{node.label}</span>
        <ChevronRight size={13} className="opacity-60" />
      </button>
      {open && (
        <div className="absolute left-full top-0 z-50 ml-1 w-[160px] rounded me-card p-1 shadow-lg">
          {node.children.map((it) => (
            <NavLink key={it.to} to={it.to} onClick={onNavigate} className="block">
              {({ isActive }) => (
                <span
                  data-selected={isActive ? 'true' : undefined}
                  className="block rounded px-2.5 py-1.5 text-[12px] me-text hover:bg-[color:var(--me-selected)] data-[selected]:bg-[color:var(--me-selected)]"
                  style={{ fontFamily: chromeFont }}
                >
                  {it.label}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}

// Caret-driven nav dropdown (Ken 2026-10-22) — a top-level tab that opens a
// small menu of pages. Styled as a peer nav tab (me-nav-tab); shows the active
// underline when the current route is one of its items (incl. a submenu's). Closes
// on outside click, Escape, and any route change. Used for "wip" (and, on KEN,
// its nested "Admin" submenu).
function NavDropdown({ label, items, chromeFont }: {
  label: string
  items: NavNode[]
  chromeFont: string
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const onSection = items.some((it) => 'children' in it
    ? it.children.some((ch) => location.pathname.startsWith(ch.to))
    : location.pathname.startsWith(it.to))
  useEffect(() => { setOpen(false) }, [location.pathname])
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])
  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-selected={onSection ? 'true' : undefined}
        className="me-nav-tab"
        style={{ fontFamily: chromeFont }}
      >
        {label}
        <ChevronDown size={13} className="opacity-60" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-[184px] rounded me-card p-1 shadow-lg">
          {items.map((it) => (
            'children' in it ? (
              <NavSubmenu key={it.label} node={it} chromeFont={chromeFont} onNavigate={() => setOpen(false)} />
            ) : (
              <NavLink key={it.to} to={it.to} onClick={() => setOpen(false)} className="block">
                {({ isActive }) => (
                  <span
                    data-selected={isActive ? 'true' : undefined}
                    className="block rounded px-2.5 py-1.5 text-[12px] me-text hover:bg-[color:var(--me-selected)] data-[selected]:bg-[color:var(--me-selected)]"
                    style={{ fontFamily: chromeFont }}
                  >
                    {it.label}
                  </span>
                )}
              </NavLink>
            )
          ))}
        </div>
      )}
    </div>
  )
}

// KEN-only inline wordmark (Ken 2026-11-01). A single editable line the brander
// clicks into and types directly in the nav — this is a demo tenant only Ken
// sees/controls, so the brand name is authored in place rather than via QA.
// The field auto-sizes to its text (an invisible sizer span mirrors the value),
// so a blank/short name lets the nav tabs shift LEFT and a long one pushes them
// RIGHT — the controls track the wordmark width. Bound to product.name, so it
// persists tenant-wide via saveProductName() (same field the QA tool writes).
// Only rendered for playhead/KEN; MOVU keeps the static wordmark untouched.
function InlineBrandName() {
  const product = useProductName()
  const [draft, setDraft] = useState(product.name)
  const ref = useRef<HTMLInputElement>(null)
  // The tenant config arrives async after mount; adopt it (and any edit from
  // another tab) only when we're NOT mid-edit, so it never clobbers typing.
  useEffect(() => {
    if (document.activeElement !== ref.current) setDraft(product.name)
  }, [product.name])
  const onChange = (v: string) => {
    setDraft(v)
    saveProductName({ ...product, name: v })
  }
  // Sizer and input MUST share identical type metrics + padding so the measured
  // width matches the rendered text exactly. Matches the old SECRETARY line.
  // Face + size are driven by the Brand font-group config (UXT-1 2026-11-03):
  // Inter Tight @ product.brandSize, matching the MOVU wordmark mechanism. Was
  // hardcoded to --me-font-data (mono) @ 26px.
  const typeStyle: CSSProperties = {
    fontFamily: FONTS[product.brandFont].family, fontSize: product.brandSize, fontWeight: 600,
    letterSpacing: '0.02em', color: '#1C1910', lineHeight: 1.1, padding: '2px 1px',
  }
  return (
    <div className="mr-4" style={{ marginLeft: 10, display: 'inline-grid', alignItems: 'center' }}>
      <span aria-hidden className="whitespace-pre" style={{ ...typeStyle, gridArea: '1 / 1', visibility: 'hidden' }}>
        {draft || 'Name'}
      </span>
      <input
        ref={ref}
        value={draft}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Name"
        aria-label="Product name — click to edit"
        title="Click to rename"
        spellCheck={false}
        className="border-0 bg-transparent outline-none focus:outline-none"
        style={{ ...typeStyle, gridArea: '1 / 1', width: '100%', minWidth: '2ch' }}
      />
    </div>
  )
}

export default function Layout() {
  const auth = useAuth()
  const user = auth.status === 'authenticated' ? auth.user : null
  // KEN/playhead: the wordmark is an inline editable line sitting directly on
  // the bare nav background — no plate, tile, or hairline edge (UXT-1 2026-11-03;
  // the sealed cream tile was retired 2026-11-01).
  const playhead = !!user && isPlayheadTenant(user.client?.code)
  // ── Client end-user interaction capture (feeds Studio → Searches) ──────
  // Enable only for genuine end-users (not superusers, not studio roles) so
  // staff activity doesn't pollute the data.
  useEffect(() => {
    configureTracking(!!user && !user.is_superuser && !user.has_studio_access)
  }, [user])
  // Route-driven traces: a 'visit' row for Stats/Playbook, and finalize any
  // open Main trace when the user navigates away from Main.
  const location = useLocation()
  const prevPathRef = useRef('')
  useEffect(() => {
    const path = location.pathname
    const prev = prevPathRef.current
    prevPathRef.current = path
    if (prev.startsWith('/workspace') && !path.startsWith('/workspace')) finalizeCurrent()
    if (path.startsWith('/stats')) traceVisit('stats')
    else if (path.startsWith('/work-product')) traceVisit('playbook')
    else if (path.startsWith('/privacy')) traceVisit('privacy')
  }, [location.pathname])
  // Experimental product wordmark (name + serif font + size). Ken drives
  // it from QA → "Product Name"; it updates here live. Color is fixed.
  const product = useProductName()
  const navColor = useNavColor()
  const navBanner = useNavBanner()
  // Publish the five font GROUPS + selected-accent as CSS vars so they update
  // live everywhere (see the .me-record/.me-summary/.me-chrome/.me-data classes
  // in index.css). The wordmark (Brand) and nav (Chrome) are styled inline
  // below from the same store.
  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--me-font-record', FONTS[product.recordFont].family)
    root.style.setProperty('--me-font-summary', FONTS[product.summaryFont].family)
    root.style.setProperty('--me-font-chrome', FONTS[product.chromeFont].family)
    root.style.setProperty('--me-font-data', FONTS[product.dataFont].family)
    root.style.setProperty('--me-accent-select', product.selectedColor)
    // Hide the matter `kind` segment on the sidebar identifier line even when
    // identifiers are exposed (Ken 2026-08-11). CSS-driven so it composes with
    // the existing data-identifiers toggle. Default on.
    root.setAttribute('data-hide-matter-kind', product.hideMatterKind ? 'on' : 'off')
  }, [product.recordFont, product.summaryFont, product.chromeFont, product.dataFont,
      product.selectedColor, product.hideMatterKind])
  // Nav-bar colour as a CSS var (the details checkbox + anything else can read it).
  // Also derive the sidebar "new matter" pip colour — a deliberately different,
  // non-urgent companion to the brand so the pip is distinct from the brand-coloured
  // newness-slider handle (Ken 2026-08-19; see ../newPipColor).
  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--me-nav-color', navColor)
    root.style.setProperty('--me-new-pip', deriveNewPipColor(navColor))
    // Uploaded control-bar banner (QA → Control Bar Banner). Layered over the
    // nav colour by .me-nav-bg; 'none' when unset (header shows just colour).
    root.style.setProperty('--me-nav-banner', navBanner ? `url("${navBanner}")` : 'none')
  }, [navColor, navBanner])

  // Render one top-level nav tab. Only Main (= Matters) takes a QA-editable
  // label; every other tab uses its static label. Admin tabs recede.
  const renderTab = ({ to, label, prominent }: (typeof tabs)[number]) => {
    const displayLabel = to === '/workspace' ? product.pageMainName : label
    return (
      <NavLink key={to} to={to}>
        {({ isActive }) => (
          <span
            data-selected={isActive ? 'true' : undefined}
            className={`me-nav-tab ${prominent ? '' : 'me-nav-recede'}`}
            style={{ fontFamily: 'var(--me-font-nav)' }}
          >
            {displayLabel}
          </span>
        )}
      </NavLink>
    )
  }

  return (
    <div className="flex h-screen flex-col bg-[color:var(--me-bg)]">
      {/* Header hairline uses per-tenant vars (default = 1px var(--me-border),
          i.e. unchanged) so KEN can restyle the rule under the nav (Ken 2026-10-22). */}
      <header
        className="me-nav-bg flex items-center gap-1 px-3 py-2"
        style={{
          borderBottom: 'var(--me-hairline-w) solid var(--me-hairline-color)',
          // KEN nav now matches MOVU (Ken 2026-11-01): no forced height, no pinned
          // background art — just the nav colour. (KEN previously had a 98px-tall
          // header with the uploaded NAV-BACKGROUND jpeg pinned right.) The KEN
          // wordmark/name treatment below is deliberately left unchanged.
        }}
      >
        {/* Product wordmark — replaces the old mono "Matters" + black
            square. An uploaded logo image (QA → Product Name) wins; otherwise
            the text wordmark, serif/sized/colored per the Product Name tool.
            The "Size — wordmark" slider drives the image height too. */}
        {(() => {
          const mark = product.brandLogo ? (
            <img
              src={product.brandLogo}
              alt={product.name || 'logo'}
              className="me-nav-wordmark block"
              style={{
                height: Math.round(product.brandSize * 1.5),
                width: 'auto',
                maxWidth: 280,
                objectFit: 'contain',
              }}
            />
          ) : (
            <div
              className="me-nav-wordmark flex items-center whitespace-nowrap leading-none"
              style={{
                fontFamily: FONTS[product.brandFont].family,
                fontSize: product.brandSize,
              }}
            >
              {product.name}
            </div>
          )
          // Playhead (KEN): a single editable wordmark line the brander types in
          // place (Ken 2026-11-01). Replaces the former two-line WORKINGTHINKING /
          // SECRETARY plate. MOVU keeps the bare static wordmark.
          return playhead ? (
            <InlineBrandName />
          ) : (
            <div className="mr-4 inline-flex items-center">{mark}</div>
          )
        })()}
        <nav className="flex flex-1 items-center gap-4">
          {/* Places: Matters (= Main) and Walls. Admin tabs render after wip. */}
          {tabs.filter((t) =>
            !t.adminOnly
            && (!t.wallsTab || (user && (user.can_manage_walls || user.can_wall_own_mail)))
          ).map(renderTab)}
          {/* Quiet — now an equal top-level nav item (Ken 2026-10-22; moved out of
              the right cluster). Still per-user gated via shadow_ui.quiet_enabled. */}
          {user && user.quiet_enabled && <QuietMenu />}
          {/* wip — dropdown of secondary pages (Stats, Playbooks, Accounting + the
              coming-soon stubs). On KEN/playhead with studio access, the admin
              tools (Studio · Settings) are nested INSIDE wip as an "Admin" submenu
              (Ken 2026-10-23), so admin is a doubly-nested pulldown. */}
          <NavDropdown
            label="wip"
            items={(playhead && user && user.has_studio_access)
              ? [...wipItems, { label: 'Admin', children: adminItems }]
              : wipItems}
            chromeFont={'var(--me-font-nav)'}
          />
          {/* Admin tooling — studio access only. MOVU keeps the separate top-level
              tabs; KEN folds them into the wip → Admin submenu above. */}
          {user && user.has_studio_access && !playhead
            && tabs.filter((t) => t.adminOnly).map(renderTab)}
        </nav>
        <div className="flex items-center gap-2 text-[12px] me-text-mute">
          {user && (
            <ActAsPicker user={user} onChange={() => auth.refresh()} />
          )}
          {/* Quiet moved into the nav row (left cluster) — Ken 2026-10-22. */}
          {/* Signed-in username + tenant code (MOVU) hidden per Ken 2026-08-11.
              "Pulls" chip removed — it's now the "Playbook" nav page. */}
          <TweaksPanel />
          <button
            onClick={() => auth.logout()}
            className="me-btn me-btn-outline"
            style={{ borderColor: 'var(--me-borderStrong)' }}
            title="Log out"
          >
            <LogOut size={playhead ? 15 : 13} />
          </button>
        </div>
      </header>
      <main className="me-scroll flex-1 overflow-hidden">
        <Outlet />
      </main>
    </div>
  )
}
